import { useState, useCallback } from 'react';
import { ROUTES } from '../routes';
import { api } from '../client';

export interface WizardProgresso {
  etapaAtual: number;
  concluido: boolean;
  concluidoEm?: string | null;
}

export interface WizardResumo {
  turnosCriados: number;
  materiasCriadas: number;
  contasEquipeCriadas: number;
  contasProfessoresCriadas: number;
  totalAlunos: number;
  concluidoEm: string;
  tokensEquipe: string[];
  tokensProfessores: string[];
}

export function useWizard() {
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const getProgresso = useCallback(async (): Promise<WizardProgresso> => {
    const { data } = await api.get(ROUTES.wizard.progresso);
    return data;
  }, []);

  const salvarEtapa = useCallback(async (etapa: number, dados: Record<string, any>) => {
    await api.post(ROUTES.wizard.salvarEtapa, { etapa, dados });
  }, []);

  const avancarEtapa = useCallback(async (etapa: number, dados: Record<string, any>) => {
    await api.post(ROUTES.wizard.avancarEtapa, { etapa, dados });
  }, []);

  const confirmar = useCallback(async (etapas: Record<string, any>): Promise<{ ok: boolean; resumo: WizardResumo }> => {
    setLoading(true);
    setErro(null);
    try {
      const { data } = await api.post(ROUTES.wizard.confirmar, { etapas });
      return data;
    } catch (e: any) {
      const msg = e?.response?.data?.message || 'Erro ao confirmar wizard';
      setErro(msg);
      throw new Error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  const reset = useCallback(async () => {
    await api.post(ROUTES.wizard.reset);
  }, []);

  /** Abre SSE e chama onEvento a cada progresso recebido */
  const abrirSSE = useCallback((onEvento: (e: { operacao: string; percentual: number; status: string; detalhe?: string }) => void) => {
    const token = (localStorage.getItem('ac_token') || sessionStorage.getItem('ac_token')) || '';
    const baseURL = (import.meta.env.VITE_API_URL || '/api/v1').replace(/\/$/, '');
    const url = `${baseURL}/wizard/progresso-sse`;

    // SSE não suporta headers nativamente — usamos fetch + ReadableStream
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const reader = res.body!.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        while (!cancelled) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const parts = buffer.split('\n\n');
          buffer = parts.pop() || '';
          for (const part of parts) {
            const dataLine = part.split('\n').find(l => l.startsWith('data:'));
            if (dataLine) {
              try { onEvento(JSON.parse(dataLine.slice(5).trim())); } catch { /* ignore */ }
            }
          }
        }
      } catch { /* ignore conexão fechada */ }
    })();
    return () => { cancelled = true; };
  }, []);

  return { loading, erro, getProgresso, salvarEtapa, avancarEtapa, confirmar, reset, abrirSSE };
}
